import React, {useState} from "react";
import {connect} from "react-redux";
import PropTypes from "prop-types";
import { withTranslation } from "react-i18next";
import isString from "lodash/isString";
import isValidIdentifier from "helpers/isValidIdentifier";
import {
    addItem,
    clearState,
    removeItem,
    saveItem,
    saveLabel,
    saveDescription,
    selectChecklist,
    reorderItems,
    addChecklistAction,
    setChecklists,
    setCurrentChecklist
} from "./redux/actions";
import ChecklistForm from "./ChecklistForm";
import {requireMinimumLength, requireAtleastOneItem} from "./helpers";

const AddChecklist = (props) => {

    const [error, setError] = useState("");

    const selectChecklistHandler = (checklist) => {
        const {selectChecklist, applyChecklist, index} = props;

        selectChecklist(checklist);

        if (!isValidIdentifier(checklist.id)) {
            applyChecklist(null, index);
        } else {
            applyChecklist(checklist, index);
        }
    };

    const onSubmit = async (event) => {
        event.stopPropagation();
        const {
            addChecklist,
            checklist,
            organisationId,
            t,
            checklists,
            setChecklists,
            setCurrentChecklist,
            setActive
        } = props;
        const {label, description} = checklist;

        const trimmedLabel = isString(label) ? label.trim() : "";
        
        if (!requireMinimumLength(trimmedLabel)) {
            setError(t("validation.string.two.characters.required"));
            return;
        }

        const {items} = checklist;
        const newItems = [...items];
        const lastItem = newItems.pop();

        if (!requireAtleastOneItem(items)) {
            setError(t("validation.required.items"));
            return;
        }

        let errors = false;

        const mappedItems = newItems.map(item => {
            const {label} = item;
            if (!requireMinimumLength(label)) {
                errors = true;
                return {...item, error: t("validation.string.two.characters.required")};
            }
            return item;
        });

        if (errors) {
            setCurrentChecklist({ label: trimmedLabel, description, items: [...mappedItems, lastItem] });
            return;
        }

        await addChecklist(organisationId)
            .then(response => {
                const {data} = response;
                const {addChecklist} = data;
                setChecklists([...checklists, addChecklist]);
                selectChecklistHandler(addChecklist);
            })
            .catch((error) => {
                console.error("[AddChecklist:onSubmit] addChecklist failed", error);
                const errorMessage = typeof error.graphQLErrors !== "undefined" ?
                    error.graphQLErrors[0].message : "checklist.creation.failed";
                setError(t(errorMessage));
            });

        setActive(false);
    };

    const {
        index,
        checklist,
        setActive,
        addItem,
        removeItem,
        saveItem,
        style,
        t,
        saveLabel,
        saveDescription,
        reorderItems,
        readOnly
    } = props;

    return (
        <div className="card checklist-list" style={style}>
            <div className="card-header">

                <div className="card-header-title">
                    {readOnly
                        ? t("checklist.preview")
                        : t("common.new.add", {what: t("checklist.singular")})}
                </div>
                <span className="card-header-icon is-pulled-right"
                      onClick={() => setActive(false)}>
                    <span className="icon">
                        <i className="fas fa-times fa-lg"/>
                    </span>
                </span>
            </div>
            <div className="card-content">
                <div className="content">
                        <ChecklistForm
                            index={index}
                            addItem={addItem}
                            saveItem={saveItem}
                            removeItem={removeItem}
                            checklist={checklist}
                            error={error}
                            saveLabel={saveLabel}
                            saveDescription={saveDescription}
                            reorderItems={reorderItems}
                            readOnly={readOnly}
                        />
                </div>
            </div>
            {!readOnly ? (
                <div className="card-footer">
                    <div
                        className="card-footer-item">
                        <div className="button is-success is-fullwidth"
                             onClick={(event) => onSubmit(event)}
                             data-testid={`save-checklist${index}`}>
                                {t("button.save.label")}
                        </div>
                    </div>
                </div>
            ) : null}
        </div>);
};

AddChecklist.propTypes = {
    index: PropTypes.number.isRequired,
    addItem: PropTypes.func.isRequired,
    addChecklist: PropTypes.func.isRequired,
    checklist: PropTypes.object,
    organisationId: PropTypes.string.isRequired,
    removeItem: PropTypes.func.isRequired,
    saveItem: PropTypes.func.isRequired,
    setActive: PropTypes.func.isRequired,
    t: PropTypes.func.isRequired,
    saveLabel: PropTypes.func.isRequired,
    saveDescription: PropTypes.func.isRequired,
    setChecklists: PropTypes.func.isRequired,
    checklists: PropTypes.array.isRequired,
    setCurrentChecklist: PropTypes.func.isRequired,
    selectChecklist: PropTypes.func.isRequired,
    applyChecklist: PropTypes.func.isRequired,
    style: PropTypes.object,
    reorderItems: PropTypes.func.isRequired,
    readOnly: PropTypes.bool
};

const mapStateToProps = (state) => {
    const {manageChecklists} = state;
    const {checklist, checklists} = manageChecklists;
    return {checklist, checklists};
};

const mapDispatchToProps = (dispatch) => {
    return {
        addItem: () => {
            dispatch(addItem());
        },
        addChecklist: (organisationId) => {
            return dispatch(addChecklistAction(organisationId));
        },
        selectChecklist: (checklist) => {
            dispatch(selectChecklist(checklist));
        },
        saveItem: (itemIndex, item) => {
            dispatch(saveItem(itemIndex, item));
        },
        removeItem: (itemIndex) => {
            dispatch(removeItem(itemIndex));
        },
        saveLabel: (label) => {
            dispatch(saveLabel(label));
        },
        saveDescription: (description) => {
            dispatch(saveDescription(description));
        },
        // setChecklist: (checklist) => {
        //     dispatch(setChecklist(checklist));
        // },
        setChecklists: (checklists) => {
            dispatch(setChecklists(checklists));
        },
        setCurrentChecklist: (checklist) => {
            dispatch(setCurrentChecklist(checklist));
        },
        reorderItems: (fromIndex, toIndex) => {
            dispatch(reorderItems(fromIndex, toIndex));
        },
        clearState: () => {
            dispatch(clearState());
        }
    };
};

export default withTranslation()(connect(mapStateToProps, mapDispatchToProps)(AddChecklist));




